import { aboutData } from '@/mocks/about';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import AboutContactForm from './components/AboutContactForm';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '布丁漫画',
        url: 'https://budingmanhua.com',
        description: '布丁漫画下拉式阅读专区，提供流畅的下拉式漫画观看体验，免费看韩漫、国漫、日漫全集，支持在线追更。',
        foundingDate: '2019',
        email: aboutData.contact.email,
        address: {
          '@type': 'PostalAddress',
          streetAddress: '望京SOHO T3-A座 2501室',
          addressLocality: '北京',
          addressRegion: '朝阳区',
          addressCountry: 'CN',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          email: aboutData.contact.email,
          telephone: aboutData.contact.phone,
          contactType: 'customer service',
          availableLanguage: ['Chinese'],
        },
      },
      {
        '@type': 'AboutPage',
        name: '关于布丁漫画 - 下拉式漫画阅读平台',
        description: '布丁漫画品牌介绍、发展历程、用户口碑及联系方式。了解布丁漫画下拉式阅读的独特魅力。',
        url: 'https://budingmanhua.com/about',
        isPartOf: { '@id': 'https://budingmanhua.com/#website' },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于布丁漫画 - 下拉式漫画阅读平台 | 布丁漫画"
        description="布丁漫画品牌介绍、发展历程及用户口碑。布丁漫画下拉式阅读专区，提供流畅的下拉式漫画观看体验，免费看韩漫、国漫、日漫全集。"
        keywords="布丁漫画,下拉式阅读,关于布丁漫画,漫画平台,免费漫画,布丁漫画网"
        canonicalUrl="https://budingmanhua.com/about"
        schema={aboutSchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        <section className="py-10 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              关于布丁漫画
            </h1>
            <p className="text-lg text-foreground-500 mb-2">{aboutData.tagline}</p>
            <p className="text-sm text-foreground-400">始于 {aboutData.founded} 年，专注漫画阅读体验</p>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-6 text-center">品牌故事</h2>
            <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed space-y-4">
              {aboutData.story.split('\n\n').map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">核心价值</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {aboutData.values.map((val) => (
                <div key={val.title} className="p-5 rounded-xl bg-background-100 border border-background-200/60 text-center">
                  <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-4">
                    <i className={`${val.icon} text-xl`}></i>
                  </span>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{val.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{val.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">发展历程</h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px" />
              <div className="space-y-6">
                {aboutData.milestones.map((ms, index) => (
                  <div key={ms.year} className={`relative flex items-start gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                      <div className="bg-background-50 rounded-xl p-4 border border-background-200/60 inline-block max-w-xs">
                        <p className="text-sm text-foreground-700">{ms.event}</p>
                      </div>
                    </div>
                    <div className="relative z-10 flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
                      <span className="text-xs font-bold">{ms.year.slice(2)}</span>
                    </div>
                    <div className="flex-1 md:hidden pl-4">
                      <span className="text-sm font-semibold text-primary-600">{ms.year}</span>
                      <p className="text-sm text-foreground-600 mt-1">{ms.event}</p>
                    </div>
                    <div className="flex-1 md:block hidden">
                      <div className="bg-background-50 rounded-xl p-4 border border-background-200/60 inline-block max-w-xs">
                        <p className="text-sm text-foreground-700">{ms.event}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-2 text-center">数据一览</h2>
            <p className="text-sm text-foreground-500 text-center mb-8">数字记录布丁漫画的每一步成长</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {aboutData.stats.map((stat) => (
                <div key={stat.label} className="p-5 rounded-xl bg-background-100 border border-background-200/60 text-center">
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-3">
                    <i className={`${stat.icon} text-lg`}></i>
                  </span>
                  <div className="text-2xl font-heading font-bold text-foreground-950">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-2 text-center">用户口碑</h2>
            <p className="text-sm text-foreground-500 text-center mb-8">听听读者们怎么说</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {aboutData.testimonials.map((t) => (
                <div key={t.name} className="p-5 rounded-xl bg-background-50 border border-background-200/60">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <span key={i} className="w-3 h-3 flex items-center justify-center text-accent-500">
                            <i className="ri-star-fill text-[10px]"></i>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-2 text-center">联系我们</h2>
            <p className="text-sm text-foreground-500 text-center mb-8">有任何问题或建议？我们很乐意倾听</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
              <div className="p-4 rounded-xl bg-background-100 border border-background-200/60 flex items-start gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-400 mb-0.5">电子邮箱</p>
                  <p className="text-sm font-medium text-foreground-900">{aboutData.contact.email}</p>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-background-100 border border-background-200/60 flex items-start gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-phone-line text-lg"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-400 mb-0.5">客服电话</p>
                  <p className="text-sm font-medium text-foreground-900">{aboutData.contact.phone}</p>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-background-100 border border-background-200/60 flex items-start gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-map-pin-line text-lg"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-400 mb-0.5">办公地址</p>
                  <p className="text-sm font-medium text-foreground-900">{aboutData.contact.address}</p>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-background-100 border border-background-200/60 flex items-start gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-time-line text-lg"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-400 mb-0.5">工作时间</p>
                  <p className="text-sm font-medium text-foreground-900">{aboutData.contact.workHours}</p>
                </div>
              </div>
            </div>

            <AboutContactForm />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}